import { useTranslation } from "react-i18next";
import { ReactComponent as Trash } from "../../assets/trash.svg";
import { selectInputFix, selectInputNoCaretFix } from "../../styleUtils";
import { ReactComponent as Plus } from "../../assets/plus.svg";
import {
  getNameKeyForOperationOffsetType,
  OperationOffsetType,
} from "../../turnaroundUtils";
import {
  deepCopy,
  getByFieldValue,
  isBlank,
  isEmptyList,
  isNullOrUndefined,
  logAnalyticsEvent,
  sortByField,
} from "../../utils";
import StartTimeOffset from "../StartTimeOffset";
import { useMainDispatchContext } from "../../MainContext";
import { useState } from "react";
import TemplateRequirementModal from "./TemplateRequirementModal";
import { ANALYTICS_EVENTS } from "../../constants";
import TemplateOperationEvents from "./TemplateOperationEvents";

function TemplateOperation(props) {
  const { t } = useTranslation();
  const dispatch = useMainDispatchContext();
  const [isRequirementsModalOpen, setIsRequirementsModalOpen] = useState(false);

  const {
    selectedOperation,
    isReadOnly,
    onChange,
    departments,
    onDelete,
    certifications,
    hasOperationEventsEnabled,
    onOpenAddEventModal,
    operationEventTypes,
    onRemoveOperationEvent,
  } = props;
  const operationToEdit = !isNullOrUndefined(selectedOperation)
    ? deepCopy(selectedOperation)
    : null;

  function handleOperationFieldChange(fieldName, value, preserveNull) {
    const operationToUpdate = deepCopy(operationToEdit);
    operationToUpdate[fieldName] =
      preserveNull && isBlank(value) ? null : value;

    if (fieldName === "offsetTypeId" && value === OperationOffsetType.FULL) {
      // No duration and no startOffsetMinutes
      operationToUpdate.startOffsetMinutes = 0;
      operationToUpdate.duration = null;
    }

    onChange(operationToUpdate);
  }

  function handleDepartmentChange(value) {
    const operationToUpdate = deepCopy(operationToEdit);
    operationToUpdate.department.uuid = value;
    onChange(operationToUpdate);
  }

  function handleOperationDeleteConfirm() {
    dispatch({
      type: "setConfirmation",
      confirmation: {
        message: t("delete_msg_web", { name: operationToEdit.type.name }),
        actionLabel: t("confirm_non_cap"),
        actionOK: () => {
          onDelete(operationToEdit.uuid);
          dispatch({
            type: "setConfirmation",
            confirmation: null,
          });
        },
        actionCancel: () => {
          dispatch({
            type: "setConfirmation",
            confirmation: null,
          });
        },
      },
    });
  }

  function handleRequirementDeleteConfirm(requirementToDelete) {
    dispatch({
      type: "setConfirmation",
      confirmation: {
        message: t("delete_msg_web", {
          name: requirementToDelete.certification.name,
        }),
        actionLabel: t("confirm_non_cap"),
        actionOK: () => {
          handleRemoveRequirement(requirementToDelete.uuid);
          dispatch({
            type: "setConfirmation",
            confirmation: null,
          });
        },
        actionCancel: () => {
          dispatch({
            type: "setConfirmation",
            confirmation: null,
          });
        },
      },
    });
  }

  function handleAddRequirement(certificationToAdd) {
    const operationToUpdate = deepCopy(operationToEdit);
    operationToUpdate.turnaroundRequirements.push({
      certification: certificationToAdd,
      quantity: 1,
      uuid: `_NEW_${certificationToAdd.uuid}`,
    });
    onChange(operationToUpdate);
  }

  function handleRemoveRequirement(requirementUuidToRemove) {
    const operationToUpdate = deepCopy(operationToEdit);
    const idxToRemove = operationToUpdate.turnaroundRequirements.findIndex(
      (i) => i.uuid === requirementUuidToRemove
    );
    if (idxToRemove > -1) {
      operationToUpdate.turnaroundRequirements.splice(idxToRemove, 1);
    }
    onChange(operationToUpdate);
    logAnalyticsEvent(
      dispatch,
      ANALYTICS_EVENTS.TURNAROUND_TEMPLATE_DELETE_REQUIREMENT
    );
  }

  function handleResourceQuantityChange(uuid, quantity) {
    const operationToUpdate = deepCopy(operationToEdit);
    for (var i = 0; i < operationToUpdate.turnaroundRequirements.length; i++) {
      const turnaroundRequirement = operationToUpdate.turnaroundRequirements[i];
      if (turnaroundRequirement.uuid === uuid) {
        turnaroundRequirement.quantity = quantity;
      }
    }
    onChange(operationToUpdate);
  }

  const typeValue = !isNullOrUndefined(operationToEdit?.offsetTypeId)
    ? operationToEdit?.offsetTypeId.toString()
    : "";
  const durationValue = !isNullOrUndefined(operationToEdit?.duration)
    ? operationToEdit?.duration?.toString()
    : "";
  const departmentValue = !isNullOrUndefined(operationToEdit?.department?.uuid)
    ? operationToEdit?.department?.uuid
    : "";

  const isFullDurationOperation =
    operationToEdit?.offsetTypeId === OperationOffsetType.FULL;

  const existingCerts = [];
  const vehiclesList = [];
  const crewList = [];
  let totalVehicles = 0;
  let totalCrew = 0;
  for (let i = 0; i < operationToEdit?.turnaroundRequirements.length; i++) {
    const turnaroundRequirement = operationToEdit?.turnaroundRequirements[i];
    if (!isBlank(turnaroundRequirement.certification.groundVehicleTypeUuid)) {
      vehiclesList.push(turnaroundRequirement);
      totalVehicles = totalVehicles + turnaroundRequirement.quantity;
    } else {
      crewList.push(turnaroundRequirement);
      totalCrew = totalCrew + turnaroundRequirement.quantity;
    }
    existingCerts.push(turnaroundRequirement.certification);
  }
  if (totalVehicles > 0) {
    totalCrew = totalCrew + totalVehicles;
  }
  sortByField(vehiclesList, "certification.name");
  sortByField(crewList, "certification.name");

  const availableCertifications = [];
  if (!isEmptyList(certifications)) {
    for (let i = 0; i < certifications.length; i++) {
      const certification = certifications[i];
      const existingCert = getByFieldValue(
        existingCerts,
        "uuid",
        certification.uuid
      );
      if (isNullOrUndefined(existingCert)) {
        availableCertifications.push(certification);
      }
    }
  }

  return (
    <>
      <div className="main-fields module">
        <div>
          <div className="template-operation-module-header">
            <div>
              <h3>{operationToEdit?.type?.name}</h3>
            </div>
            <div>
              <button
                onClick={handleOperationDeleteConfirm}
                className="alternate"
              >
                <Trash /> {t("delete")}
              </button>
            </div>
          </div>
          <div className="field-row">
            <div className="field-values">
              <label>{t("department")}</label>
              <div className="operation-department">
                <select
                  value={departmentValue}
                  style={selectInputFix}
                  onChange={(e) => {
                    handleDepartmentChange(e.target.value);
                  }}
                  disabled={isReadOnly}
                >
                  {!isEmptyList(departments) &&
                    departments.map((department) => (
                      <option key={department.uuid} value={department.uuid}>
                        {department.name}
                      </option>
                    ))}
                </select>
              </div>
            </div>
            <div className="field-values">
              <label>{t("type")}</label>
              <div>
                <select
                  value={typeValue}
                  style={selectInputFix}
                  disabled={isReadOnly}
                  onChange={(e) => {
                    handleOperationFieldChange(
                      "offsetTypeId",
                      parseInt(e.target.value)
                    );
                  }}
                >
                  <option value={OperationOffsetType.INBOUND}>
                    {t(
                      getNameKeyForOperationOffsetType(
                        OperationOffsetType.INBOUND
                      )
                    )}
                  </option>
                  <option value={OperationOffsetType.OUTBOUND}>
                    {t(
                      getNameKeyForOperationOffsetType(
                        OperationOffsetType.OUTBOUND
                      )
                    )}
                  </option>
                  <option value={OperationOffsetType.FULL}>
                    {t(
                      getNameKeyForOperationOffsetType(OperationOffsetType.FULL)
                    )}
                  </option>
                </select>
              </div>
            </div>
            <div className="field-values">
              <label>{t("start_time_offset")}</label>
              <div>
                <StartTimeOffset
                  operationToEdit={operationToEdit}
                  onChange={(value) => {
                    handleOperationFieldChange("startOffsetMinutes", value);
                  }}
                  disabled={isReadOnly}
                />
              </div>
            </div>
            <div className="field-values">
              <label>
                {t("duration")} ({t("minutes").toLowerCase()})
              </label>
              <div className="operation-duration">
                <select
                  value={durationValue}
                  style={selectInputFix}
                  onChange={(e) => {
                    handleOperationFieldChange(
                      "duration",
                      isBlank(e.target.value) ? "" : parseInt(e.target.value),
                      true
                    );
                  }}
                  disabled={isReadOnly || isFullDurationOperation}
                >
                  <option value="">{t("no_duration")}</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                  <option value="5">5</option>
                  <option value="10">10</option>
                  <option value="15">15</option>
                  <option value="20">20</option>
                  <option value="25">25</option>
                  <option value="30">30</option>
                  <option value="35">35</option>
                  <option value="40">40</option>
                  <option value="45">45</option>
                  <option value="50">50</option>
                  <option value="55">55</option>
                  <option value="60">60</option>
                  <option value="65">65</option>
                  <option value="70">70</option>
                  <option value="75">75</option>
                  <option value="80">80</option>
                  <option value="85">85</option>
                  <option value="90">90</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="resource-fields">
        <div className="module-header">
          <div>
            <h3>{t("operation_requirements")}</h3>
          </div>
          <div>
            {!isReadOnly && !isEmptyList(availableCertifications) && (
              <button
                className="alternate"
                onClick={() => {
                  setIsRequirementsModalOpen(true);
                }}
              >
                <Plus />
                {t("add_requirement")}
              </button>
            )}
          </div>
        </div>
        <div className="module">
          <div>
            <div className="resource-fields-header">
              <div>
                <h3>{t("vehicles")}</h3>
              </div>
              <div>
                {`${t("total")}: ${totalVehicles} ${
                  totalVehicles === 1 ? t("gse") : t("vehicles")
                }`}
              </div>
            </div>
            <div className="resource-fields-rows">
              {vehiclesList.length === 0 && (
                <div className="empty-state de-emphasize">
                  {t("no_gse_requirements")}
                </div>
              )}
              {vehiclesList.length > 0 &&
                vehiclesList.map((item) => (
                  <div className="resource-fields-row actions" key={item.uuid}>
                    <div>
                      <select
                        value={item.quantity.toString()}
                        style={selectInputFix}
                        onChange={(e) => {
                          handleResourceQuantityChange(
                            item.uuid,
                            parseInt(e.target.value)
                          );
                        }}
                        disabled={isReadOnly}
                      >
                        <option value={0}>0</option>
                        <option value={1}>1</option>
                        <option value={2}>2</option>
                        <option value={3}>3</option>
                        <option value={4}>4</option>
                        <option value={5}>5</option>
                        <option value={6}>6</option>
                        <option value={7}>7</option>
                        <option value={8}>8</option>
                        <option value={9}>9</option>
                        <option value={10}>10</option>
                      </select>
                    </div>
                    <div>{item.certification.name}</div>
                    <div>
                      {!isReadOnly && (
                        <Trash
                          className="button-icon"
                          onClick={() => {
                            handleRequirementDeleteConfirm(item);
                          }}
                        />
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
        <div className="module">
          <div>
            <div className="resource-fields-header">
              <div>
                <h3>{t("crew_members")}</h3>
              </div>
              <div>
                {`${t("total")}: ${totalCrew} ${
                  totalCrew === 1
                    ? t("crew_member").toLowerCase()
                    : t("crew_members").toLowerCase()
                }`}
              </div>
            </div>

            <div className="resource-fields-rows">
              {crewList.length === 0 && vehiclesList.length === 0 && (
                <div className="empty-state de-emphasize">
                  {t("no_crew_requirements")}
                </div>
              )}
              {
                /** Show number of equipment operators - one per vehicle specified **/
                totalVehicles > 0 && (
                  <div
                    className="resource-fields-row actions"
                    key={"equipment_operator"}
                  >
                    <div>
                      <select
                        value={totalVehicles}
                        style={selectInputNoCaretFix}
                        disabled={true}
                      >
                        <option value={totalVehicles}>{totalVehicles}</option>
                      </select>
                    </div>
                    <div>{t("equipment_operator")}</div>
                    <div></div>
                  </div>
                )
              }
              {crewList.length > 0 &&
                crewList.map((item) => (
                  <div className="resource-fields-row actions" key={item.uuid}>
                    <div>
                      <select
                        value={item.quantity.toString()}
                        style={selectInputFix}
                        onChange={(e) => {
                          handleResourceQuantityChange(
                            item.uuid,
                            parseInt(e.target.value)
                          );
                        }}
                        disabled={isReadOnly}
                      >
                        <option value={0}>0</option>
                        <option value={1}>1</option>
                        <option value={2}>2</option>
                        <option value={3}>3</option>
                        <option value={4}>4</option>
                        <option value={5}>5</option>
                        <option value={6}>6</option>
                      </select>
                    </div>
                    <div>{item.certification.name}</div>
                    <div>
                      {!isReadOnly && (
                        <Trash
                          className="button-icon"
                          onClick={() => {
                            handleRequirementDeleteConfirm(item);
                          }}
                        />
                      )}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </div>
      </div>
      {hasOperationEventsEnabled && (
        <TemplateOperationEvents
          operation={operationToEdit}
          operationEventTypes={operationEventTypes}
          isReadOnly={isReadOnly}
          onOpenAddEventModal={onOpenAddEventModal}
          onRemoveOperationEvent={(eventType) => {
            onRemoveOperationEvent(operationToEdit.uuid, eventType);
          }}
        />
      )}
      {isRequirementsModalOpen && (
        <TemplateRequirementModal
          certifications={availableCertifications}
          isOpen={isRequirementsModalOpen}
          onClose={() => {
            setIsRequirementsModalOpen(false);
          }}
          onAdd={(certificationToAdd) => {
            setIsRequirementsModalOpen(false);
            handleAddRequirement(certificationToAdd);
            logAnalyticsEvent(
              dispatch,
              ANALYTICS_EVENTS.TURNAROUND_TEMPLATE_ADD_REQUIREMENT
            );
          }}
        />
      )}
    </>
  );
}

export default TemplateOperation;
